import { useEffect, useState, Suspense, lazy } from "react";
import "@/App.css";
import { HashRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import InstallBanner from "@/components/InstallBanner";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import { Toaster } from "@/components/ui/sonner";

// Each page loads as its own chunk instead of all bundling into one big
// main.js — someone opening Create Glitch shouldn't have to download (and
// have their browser parse) Admin's role editor or Reports' PDF/Excel
// export libraries (jsPDF, xlsx) before they can see anything.
const CreateGlitch = lazy(() => import("@/pages/CreateGlitch"));
const StatusBoard = lazy(() => import("@/pages/StatusBoard"));
const ClosedToday = lazy(() => import("@/pages/ClosedToday"));
const GuestLog = lazy(() => import("@/pages/GuestLog"));
const Reports = lazy(() => import("@/pages/Reports"));
const Admin = lazy(() => import("@/pages/Admin"));

function PageFallback() {
  return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-[#4e73df]" />
    </div>
  );
}

function Shell({ children }) {
  const [hasBanner, setHasBanner] = useState(false);
  useEffect(() => {
    const check = () =>
      setHasBanner(
        !!document.querySelector('[data-testid="install-banner"]')
      );
    const t = setInterval(check, 500);
    return () => clearInterval(t);
  }, []);
  return <Layout hasBanner={hasBanner}>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute page="new_glitch"><Shell><CreateGlitch /></Shell></ProtectedRoute>} />
        <Route path="/status" element={<ProtectedRoute page="status"><Shell><StatusBoard /></Shell></ProtectedRoute>} />
        <Route path="/closed" element={<ProtectedRoute page="closed"><Shell><ClosedToday /></Shell></ProtectedRoute>} />
        <Route path="/guest-log" element={<ProtectedRoute page="guest_log"><Shell><GuestLog /></Shell></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute page="reports"><Shell><Reports /></Shell></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Shell><Admin /></Shell></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        const swUrl = new URL("sw.js", document.baseURI).href;
        navigator.serviceWorker.register(swUrl).catch(() => {});
      });
    }
  }, []);

  return (
    <div className="App">
      <HashRouter>
        <AuthProvider>
          <InstallBanner />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </HashRouter>
    </div>
  );
}

export default App;
